import {
  ArrowClockwiseIcon,
  BracketsCurlyIcon,
  LinkIcon,
  LockSimpleIcon,
  type Icon,
} from '@phosphor-icons/react'

// The signed-out screen reuses the landing page's motion and button classes
// (landing-up, landing-event, landing-pulse-dot, landing-btn): it is the same
// hero language, retargeted at webhooks. The import makes that dependency
// explicit rather than leaning on Landing having loaded it first.
import '../landing/landing.css'
import './webhooks.css'

import { CURRI_VERSION, useAuth } from '../../auth/AuthProvider.tsx'
import { navigateOnPlainClick } from '../../shell/links.ts'
import { CONSOLE } from '../../theme/console.ts'
import { BarcodeStamp, BARCODE_SERIAL } from '../landing/Barcode.tsx'
import { HeroGlow } from '../landing/HeroGlow.tsx'
import { useCyclingFeed } from '../landing/useCyclingFeed.ts'

// The webhooks dashboard's signed-out state, from the "Webhooks Signed Out"
// design: instead of the shell's generic "sign in to see this page" prompt,
// the screen makes the case for signing in — what the dashboard unlocks on the
// left, and a sample attempt on the right so the value is visible before a
// credential exists. Every payload shown here is fabricated and labelled as
// such; nothing leaks, because nothing real is fetched.

const WEBHOOK_DOCS_PATH = '/docs/webhooks'

/** Where "Sign up as an Integrator" lands: the welcome page's Access section,
 * which carries the request-access flow — the closest thing to a signup until
 * a real one exists. */
const INTEGRATOR_SIGNUP_PATH = '/docs/graphql#access'

// The design marks the mock 401 in amber. A literal: the portal's aliases
// (theme/tokens.css) carry no warning-text colour, same as the landing page's
// button greens.
const UNAUTHORIZED_AMBER = '#B45309'

const PERKS: readonly { icon: Icon; title: string; body: string }[] = [
  {
    icon: LinkIcon,
    title: 'Point it anywhere',
    body: 'set the URL and your own auth header per environment.',
  },
  {
    icon: BracketsCurlyIcon,
    title: 'See the exact body',
    body: 'every attempt, with the response your endpoint returned.',
  },
  {
    icon: ArrowClockwiseIcon,
    title: 'Resend in one click',
    body: 'replay anything that failed without touching your code.',
  },
]

// The real partner-visible `status` values (content/graphql/
// webhooks-errors-statuses/statuses.md; emitted by the sender in
// packages/curri-delivery-webhooks/src/curri.ts) — there are no named event topics
// to subscribe to, so the feed cycles four statuses that tell a delivery's
// arc. Blurbs paraphrase the statuses doc. Amber for the middle beat rather
// than the landing feed's second green: two greens are indistinguishable at
// 6px, and the arc should read early → moving → in transit → done.
const FEED_SOURCE = [
  { name: 'pending', dot: '#2563EB', meta: 'booked, no driver' },
  { name: 'en_route_to_origin', dot: '#F59E0B', meta: 'heading to pickup' },
  { name: 'en_route_to_destination', dot: '#7C5CFC', meta: 'loaded, on the way' },
  { name: 'delivered', dot: '#05A468', meta: 'images attached' },
] as const

interface FeedEvent {
  id: number
  name: string
  dot: string
  meta: string
}

// Module-level on purpose: `useCyclingFeed` takes it as an effect dependency.
const makeFeedEvent = (index: number): Omit<FeedEvent, 'id'> =>
  FEED_SOURCE[index % FEED_SOURCE.length]

const SignInCta = () => {
  const { state, signIn } = useAuth()
  // Covers both the click (signIn sets it before redirecting) and a resume in
  // flight at first paint — the same state SessionControl reads, with the same
  // label, so the rail and the page never disagree about what is happening.
  const signingIn = state.status === 'authenticating'

  return (
    <button
      type="button"
      className="landing-btn landing-btn-brand"
      onClick={signingIn ? undefined : signIn}
      style={{ fontFamily: 'inherit', cursor: signingIn ? 'default' : 'pointer' }}
    >
      {signingIn ? 'Signing in…' : 'Sign in'}
    </button>
  )
}

const Pitch = ({ onNavigate }: { onNavigate: (path: string) => void }) => (
  <div style={{ flex: '1 1 400px', minWidth: 'min(340px, 100%)', maxWidth: 520 }}>
    <div
      className="landing-up"
      style={{ display: 'flex', alignItems: 'center', gap: 14 }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: 'var(--curri-text-secondary)',
        }}
      >
        BUILD · WEBHOOKS
      </span>
      <BarcodeStamp tone="strong" />
    </div>

    <h1
      className="landing-up"
      style={{
        margin: '14px 0 0',
        fontSize: 'clamp(40px, 4.5vw, 54px)',
        lineHeight: 1.1,
        animationDelay: '0.03s',
      }}
    >
      Webhooks
    </h1>
    <p
      className="landing-up"
      style={{
        margin: '16px 0 0',
        fontSize: 17,
        lineHeight: 1.6,
        color: 'var(--curri-text-secondary)',
        maxWidth: '44ch',
        textWrap: 'pretty',
        animationDelay: '0.06s',
      }}
    >
      Sign in to point Curri at your endpoint, set your auth header, and read
      back every attempt with the exact body we POSTed.
    </p>

    <div
      className="landing-up"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginTop: 26,
        animationDelay: '0.1s',
      }}
    >
      <SignInCta />
      {/* An anchor styled as the design's secondary button, for the same
          reasons as the landing hero's CTAs (see Hero.tsx). */}
      <a
        className="landing-btn landing-btn-outline"
        href={WEBHOOK_DOCS_PATH}
        onClick={navigateOnPlainClick(onNavigate, WEBHOOK_DOCS_PATH)}
      >
        Read the webhook docs
      </a>
    </div>

    <div
      className="landing-up"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginTop: 32,
        animationDelay: '0.16s',
      }}
    >
      {PERKS.map(perk => (
        <div
          key={perk.title}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}
        >
          <perk.icon
            size={17}
            color="var(--curri-accent)"
            style={{ flex: 'none', marginTop: 2 }}
          />
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--curri-text-secondary)',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--curri-text)' }}>
              {perk.title}
            </span>{' '}
            — {perk.body}
          </div>
        </div>
      ))}
    </div>

    <div
      className="landing-up"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px 40px',
        marginTop: 34,
        animationDelay: '0.22s',
      }}
    >
      <div
        style={{
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--curri-text-secondary)',
        }}
      >
        no account yet?
        <br />
        <a
          href={INTEGRATOR_SIGNUP_PATH}
          onClick={navigateOnPlainClick(onNavigate, INTEGRATOR_SIGNUP_PATH)}
          style={{ fontWeight: 600 }}
        >
          Sign up as an Integrator today <span aria-hidden="true">→</span>
        </a>
      </div>
      <div
        style={{
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--curri-text-tertiary)',
        }}
      >
        GET /api/webhooks <span aria-hidden="true">→</span>{' '}
        <span style={{ color: UNAUTHORIZED_AMBER, fontWeight: 600 }}>
          401 unauthorized
        </span>
        <br />
        curri-version {CURRI_VERSION}
      </div>
    </div>
  </div>
)

const Key = ({ children }: { children: string }) => (
  <>
    <span style={{ color: CONSOLE.textBright }}>&quot;{children}&quot;</span>
    <span style={{ color: CONSOLE.muted }}>:</span>
  </>
)

const Str = ({ children }: { children: string }) => (
  <span style={{ color: CONSOLE.accent }}>&quot;{children}&quot;</span>
)

// `false` — same literal colour the signed-in PayloadPreview uses.
const Lit = ({ children }: { children: string }) => (
  <span style={{ color: '#7DD3FC' }}>{children}</span>
)

// Hand-coloured like the landing Snippet rather than run through
// PayloadPreview's tokenizer: this body is a fixture, and the design colours
// the colons separately from the keys, which the tokenizer folds together.
//
// The shape mirrors what the sender actually POSTs (apps/curri-workers/src/
// webhooks/curri.ts): a flat delivery snapshot — no event envelope, no nested
// `delivery` object. Trimmed to the fields that make the case for the body:
// the delivery id and status, the partner's own reference numbers echoed
// back, the driver, and the delivery images. Cut: origin/destination (16
// lines of address the partner sent us), the quoted-Decimal price/distance/
// travel-time noise, and fields that are null or empty on a delivered fire.
// Ids are fixtures in the real formats (del_ + 10 chars, C- tracking hash —
// elided with … where a real value would overflow the card).
const SampleJson = () => (
  <pre
    style={{
      margin: 0,
      padding: '16px 20px',
      overflow: 'hidden',
      fontFamily: 'var(--curri-font-mono)',
      fontSize: 11.5,
      lineHeight: 1.75,
      color: CONSOLE.text,
    }}
  >
    {'{\n  '}
    <Key>id</Key> <Str>del_9K4TQMBX7R</Str>
    {',\n  '}
    <Key>status</Key> <Str>delivered</Str>
    {',\n  '}
    <Key>trackingUrl</Key> <Str>https://app.curri.com/track/C-9dW3xLpQvR…</Str>
    {',\n  '}
    <Key>meta</Key>
    {' { '}
    <Key>orderNumber</Key> <Str>SO-88431</Str>
    {', '}
    <Key>poNumber</Key> <Str>PO-2210</Str>
    {' },\n  '}
    <Key>driver</Key>
    {' {\n    '}
    <Key>firstName</Key> <Str>Marisol</Str>
    {',\n    '}
    <Key>phoneNumber</Key> <Str>+13235550142</Str>
    {'\n  },\n  '}
    <Key>images</Key>
    {' ['}
    <Str>https://…/pod-9K4TQMBX7R.jpg</Str>
    {'],\n  '}
    <Key>deliveredAt</Key> <Str>2026-08-14T16:44:09.000Z</Str>
    {',\n  '}
    <Key>isSandbox</Key> <Lit>false</Lit>
    {'\n}'}
    <span className="webhook-caret" style={{ color: CONSOLE.accent }}>
      ▌
    </span>
  </pre>
)

const EventsPreview = ({ events }: { events: FeedEvent[] }) => (
  <div style={{ borderTop: `1px solid ${CONSOLE.line}` }}>
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 20px 0' }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: CONSOLE.muted,
        }}
      >
        STATUS UPDATES YOU&apos;LL RECEIVE
      </span>
    </div>
    {/* `column-reverse` puts the newest event on top while the array stays
        append-only; the fixed height clips the tail rather than growing the
        card — same shape as the landing console's feed. */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column-reverse',
        justifyContent: 'flex-end',
        gap: 10,
        height: 88,
        overflow: 'hidden',
        padding: '12px 20px 14px',
      }}
    >
      {events.map(event => (
        <div
          key={event.id}
          className="landing-event"
          style={{ display: 'flex', alignItems: 'center', gap: 9 }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: event.dot,
              flex: 'none',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 12,
              fontWeight: 500,
              color: CONSOLE.textBright,
            }}
          >
            {event.name}
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: CONSOLE.muted,
            }}
          >
            {event.meta}
          </span>
        </div>
      ))}
    </div>
  </div>
)

const SampleAttempt = () => {
  const events = useCyclingFeed(makeFeedEvent)

  return (
    <div
      className="landing-up"
      style={{
        flex: '1 1 440px',
        minWidth: 'min(400px, 100%)',
        maxWidth: 560,
        animationDelay: '0.1s',
      }}
    >
      <div
        style={{
          background: CONSOLE.surface,
          border: `1px solid ${CONSOLE.line}`,
          borderRadius: 14,
          boxShadow: 'var(--shadow-elevation-overlay)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: `1px solid ${CONSOLE.line}`,
          }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: CONSOLE.muted,
            }}
          >
            SAMPLE ATTEMPT
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 999,
              padding: '3px 9px',
            }}
          >
            <LockSimpleIcon color={CONSOLE.subdued} size={12} />
            <span
              style={{
                fontFamily: 'var(--curri-font-mono)',
                fontSize: 10.5,
                color: CONSOLE.subdued,
              }}
            >
              your data is hidden
            </span>
          </span>
        </div>

        <SampleJson />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '11px 20px',
            borderTop: `1px solid ${CONSOLE.line}`,
          }}
        >
          <span
            className="landing-pulse-dot"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: CONSOLE.live,
            }}
          />
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 12,
              color: CONSOLE.accent,
            }}
          >
            200 OK
          </span>
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 12,
              color: CONSOLE.muted,
            }}
          >
            · sandbox endpoint
          </span>
          <span style={{ flex: 1 }} />
          {/* The real cadence (a 20s cron re-POSTs until the delivery
              terminates) — not a response time, which the dashboard does not
              record and so must not promise. */}
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: CONSOLE.muted,
            }}
          >
            every 20s
          </span>
        </div>

        <EventsPreview events={events} />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 14,
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 11,
          color: 'var(--curri-text-tertiary)',
        }}
      >
        <span>sample payload — not your account</span>
        <span style={{ flex: 1, height: 1, background: 'var(--curri-border)' }} />
        <span>{BARCODE_SERIAL}</span>
      </div>
    </div>
  )
}

export const SignedOut = ({
  onNavigate,
}: {
  onNavigate: (path: string) => void
}) => (
  <div
    style={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative' }}
  >
    {/* The landing glow, trimmed to two blobs for this quieter screen. */}
    <HeroGlow blobCount={2} height={640} />

    <div
      style={{
        position: 'relative',
        maxWidth: 1128,
        margin: '0 auto',
        padding: '96px 56px 40px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 48,
          alignItems: 'center',
        }}
      >
        <Pitch onNavigate={onNavigate} />
        <SampleAttempt />
      </div>
    </div>
  </div>
)
